import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { buildApiUrl } from "../helpers/base_url";
import { useNotification } from "../components/Header/NotificationContext";

const Notifications: React.FC = () => {
  const navigate = useNavigate();
  const { resetUnread } = useNotification();
  const [user, setUser] = useState<any>(null);
  const [notifications, setNotifications] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Load user data on mount
  useEffect(() => {
    try {
      const parsed = JSON.parse(localStorage.getItem("user") || "null");
      if (!parsed) {
        navigate("/login");
        return;
      }
      setUser(parsed);
    } catch {
      navigate("/login");
    }
  }, [navigate]);

  // Fetch notifications
  useEffect(() => {
    const fetchNotifications = async () => {
      if (!user) return;

      setLoading(true);
      setError("");

      try {
        const token = localStorage.getItem("accessToken");
        if (!token) {
          throw new Error("Not authenticated");
        }

        // Try different possible notification endpoints
        let response;
        let data;

        // Try client-specific notifications first
        if (user.actorId) {
          try {
            response = await fetch(
              buildApiUrl(`/api/notifications/client/${user.actorId}`),
              {
                headers: {
                  Authorization: `Bearer ${token}`,
                },
              }
            );
            if (response.ok) {
              data = await response.json();
              setNotifications(data.data || data.notifications || []);
              resetUnread();
              setLoading(false);
              return;
            }
          } catch (e) {
            // Continue to try other endpoints
          }
        }

        // Try general notifications endpoint
        try {
          response = await fetch(buildApiUrl("/api/notifications"), {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });
          if (response.ok) {
            data = await response.json();
            setNotifications(data.data || data.notifications || []);
            resetUnread();
          } else if (response.status === 404) {
            // Endpoint doesn't exist yet, show empty state
            setNotifications([]);
          } else {
            throw new Error(`Failed to fetch notifications (${response.status})`);
          }
        } catch (e: any) {
          // If endpoint doesn't exist, show empty state instead of error
          if (e.message?.includes("404") || e.message?.includes("Failed to fetch")) {
            setNotifications([]);
          } else {
            throw e;
          }
        }
      } catch (err: any) {
        setError(err.message || "Failed to load notifications");
        setNotifications([]);
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      fetchNotifications();
    }
  }, [user, resetUnread]);

  if (!user) {
    return (
      <div
        style={{
          minHeight: "60vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#dc3545",
          fontSize: 22,
        }}
      >
        Loading...
      </div>
    );
  }

  return (
    <div
      style={{
        background: "linear-gradient(120deg, #f8f8ff 0%, #e0e7ff 100%)",
        minHeight: "100vh",
        padding: "48px 0",
      }}
    >
      <div
        style={{
          maxWidth: 800,
          margin: "0 auto",
          background: "#fff",
          borderRadius: 18,
          boxShadow: "0 4px 24px #eee",
          padding: 40,
        }}
      >
        <h2
          style={{
            color: "#6C63FF",
            fontWeight: 800,
            fontSize: 32,
            marginBottom: 24,
            marginTop: 0,
          }}
        >
          Notifications
        </h2>

        {loading ? (
          <div
            style={{
              textAlign: "center",
              padding: 48,
              color: "#6C63FF",
            }}
          >
            <svg
              width="48"
              height="48"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              style={{ margin: "0 auto 16px", display: "block" }}
            >
              <circle
                cx="12"
                cy="12"
                r="10"
                stroke="#6C63FF"
                strokeWidth="2"
                strokeLinecap="round"
                strokeDasharray="31.416"
                strokeDashoffset="31.416"
                fill="none"
                opacity="0.3"
              >
                <animate
                  attributeName="stroke-dasharray"
                  dur="2s"
                  values="0 31.416;15.708 15.708;0 31.416;0 31.416"
                  repeatCount="indefinite"
                />
                <animate
                  attributeName="stroke-dashoffset"
                  dur="2s"
                  values="0;-15.708;-31.416;-31.416"
                  repeatCount="indefinite"
                />
              </circle>
              <circle
                cx="12"
                cy="12"
                r="10"
                stroke="#6C63FF"
                strokeWidth="2"
                strokeLinecap="round"
                strokeDasharray="31.416"
                strokeDashoffset="15.708"
                fill="none"
              >
                <animate
                  attributeName="stroke-dasharray"
                  dur="2s"
                  values="0 31.416;15.708 15.708;0 31.416;0 31.416"
                  repeatCount="indefinite"
                />
                <animate
                  attributeName="stroke-dashoffset"
                  dur="2s"
                  values="0;-15.708;-31.416;-31.416"
                  repeatCount="indefinite"
                />
              </circle>
            </svg>
            <div style={{ color: "#333", fontWeight: 500, fontSize: 16 }}>
              Loading notifications...
            </div>
          </div>
        ) : error ? (
          <div
            style={{
              textAlign: "center",
              padding: 48,
              color: "#dc3545",
            }}
          >
            <svg
              width="48"
              height="48"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              style={{ margin: "0 auto 16px", display: "block", opacity: 0.7 }}
            >
              <circle
                cx="12"
                cy="12"
                r="10"
                stroke="#dc3545"
                strokeWidth="2"
                fill="none"
              />
              <path
                d="M12 8V12M12 16H12.01"
                stroke="#dc3545"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
            <div style={{ color: "#333", fontWeight: 500, fontSize: 16, marginBottom: 8 }}>
              {error}
            </div>
            <button
              onClick={() => window.location.reload()}
              style={{
                padding: "8px 16px",
                background: "#6C63FF",
                color: "#fff",
                border: "none",
                borderRadius: 6,
                cursor: "pointer",
                fontWeight: 600,
                fontSize: 14,
              }}
            >
              Retry
            </button>
          </div>
        ) : notifications.length === 0 ? (
          <div
            style={{
              textAlign: "center",
              padding: 48,
              color: "#666",
            }}
          >
            <svg
              width="64"
              height="64"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              style={{ margin: "0 auto 16px", display: "block", opacity: 0.5 }}
            >
              <path
                d="M18 8A6 6 0 0 0 6 8C6 11.3137 3.31371 14 0 14H18C14.6863 14 12 11.3137 12 8Z"
                stroke="#888"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                fill="none"
              />
              <path
                d="M18 8C18 11.3137 20.6863 14 24 14H6C9.31371 14 12 11.3137 12 8"
                stroke="#888"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                fill="none"
              />
              <path
                d="M9 19L12 22L15 19"
                stroke="#888"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <div style={{ color: "#333", fontWeight: 500, fontSize: 18, marginBottom: 8 }}>
              No notifications yet
            </div>
            <div style={{ color: "#666", fontSize: 14 }}>
              You'll see your notifications here when they arrive.
            </div>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {notifications.map((notification: any, index: number) => (
              <div
                key={notification.id || index}
                style={{
                  background: notification.read ? "#f8f8ff" : "#fff",
                  border: `1px solid ${notification.read ? "#eee" : "#6C63FF"}`,
                  borderRadius: 12,
                  padding: 20,
                  cursor: "pointer",
                  transition: "all 0.2s",
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.boxShadow = "0 2px 8px rgba(108, 99, 255, 0.15)";
                  e.currentTarget.style.transform = "translateY(-2px)";
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.boxShadow = "none";
                  e.currentTarget.style.transform = "translateY(0)";
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 12,
                  }}
                >
                  <div
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: "50%",
                      background: notification.read
                        ? "#e0e7ff"
                        : "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M18 8A6 6 0 0 0 6 8C6 11.3137 3.31371 14 0 14H18C14.6863 14 12 11.3137 12 8Z"
                        stroke="white"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M9 19L12 22L15 19"
                        stroke="white"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        color: "#222",
                        fontWeight: notification.read ? 600 : 700,
                        fontSize: 16,
                        marginBottom: 4,
                      }}
                    >
                      {notification.title || notification.message || "Notification"}
                    </div>
                    {notification.message && notification.title && (
                      <div
                        style={{
                          color: "#333",
                          fontSize: 14,
                          lineHeight: 1.5,
                          marginBottom: 8,
                        }}
                      >
                        {notification.message}
                      </div>
                    )}
                    <div
                      style={{
                        color: "#666",
                        fontSize: 12,
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                      }}
                    >
                      {notification.createdAt && (
                        <span>
                          {new Date(notification.createdAt).toLocaleString()}
                        </span>
                      )}
                      {!notification.read && (
                        <span
                          style={{
                            background: "#6C63FF",
                            color: "#fff",
                            padding: "2px 8px",
                            borderRadius: 10,
                            fontSize: 11,
                            fontWeight: 600,
                          }}
                        >
                          New
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Notifications;

